import { useMsal } from '@azure/msal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import HookFormInput from '@/components/HookFormInput';
import { usePostData } from '@/helpers/hooks';
import { useConfirm } from '@/store/useConfirmDialogStore';

interface Props {
  handleForgotPWClick: (b: boolean) => void;
}

interface LoginUser {
  email: string;
  password: string;
}

const LoginForm: React.FC<Props> = ({ handleForgotPWClick }: Props) => {
  const [showMFA, setShowMFA] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const confirm = useConfirm();

  const formSchema = z.object({
    email: z.string().email().min(1, 'Please provide your email.'),
    password: z.string().min(1, 'Please provide your password.')
  });
  type FormFields = z.infer<typeof formSchema>;

  const methods = useForm<FormFields>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' }
  });
  const loginMutate = usePostData<string, any, LoginUser>({ url: '/login/login' });
  const validateMFAMutate = usePostData<any, any, { email: string; code: string }>({
    url: '/login/validateMFA',
    options: {
      onSuccess: () => {
        window.location.href = '/';
      },
      onError: () => {
        enqueueSnackbar('Validation failed. Please try again.', {
          variant: 'error'
        });
      }
    }
  });
  const { handleSubmit } = methods;

  const handleVerifyClick = async () => {
    const isValidMFACode = /^[a-zA-Z0-9]{4,6}$/.test(code);
    if (!isValidMFACode) {
      setCodeError('Code format is invalid');
    } else {
      setCodeError('');
      await validateMFAMutate.mutateAsync({ email: email, code: code });
    }
  };

  const onSubmit = async (data: FormFields) => {
    const userName = data.email;
    const passWord = data.password;
    try {
      const result = await loginMutate.mutateAsync({
        email: userName,
        password: passWord
      });
      if (result) {
        if (result === 'signedIn') {
          window.location.href = '/';
        } else {
          await confirm({
            title: 'Error',
            message: result,
            isModal: true
          });
        }
      } else {
        setShowMFA(true);
        setEmail(data.email);
      }
    } finally {
      // Do nothing
    }
  };
  return (
    <>
      {showMFA ? (
        <>
          <Typography variant={'h3'} mt={0}>
            Multi-factor authentication
          </Typography>
          <Typography
            variant={'body2'}
            sx={{
              mt: 3,
              mb: 0
            }}
          >
            A verification code was sent to your device.
          </Typography>
          <Typography
            variant={'body2'}
            sx={{
              mt: 0,
              mb: 3
            }}
          >
            Please enter the code below:
          </Typography>
          <TextField
            size='small'
            label='Verification code'
            value={code}
            onChange={e => setCode(e.target.value)}
            error={!!codeError}
            helperText={codeError}
          >
            Verification code
          </TextField>
          <Button type='button' variant={'contained'} sx={{ marginLeft: 4 }} onClick={handleVerifyClick}>
            Verify
          </Button>
        </>
      ) : (
        <>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} id='login-form' className='space-y-2'>
              <HookFormInput
                name='email'
                label='Email address'
                variant='filled'
                data-testid='email-address'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '54px',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    '& fieldset': {
                      border: '1px solid white'
                    }
                  }
                }}
              />
              <HookFormInput
                name='password'
                label='Password'
                variant='filled'
                data-testid='password'
                forPassword
                sx={{
                  marginTop: 1,
                  '& .MuiInputBase-root': {
                    height: '54px',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    '& fieldset': {
                      border: '1px solid white'
                    }
                  }
                }}
              />
              <div className='flex items-center justify-between pt-4'>
                <Button
                  type='button'
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    color: '#1A1A1A',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => handleForgotPWClick(true)}
                >
                  Forgot password?
                </Button>
                <LoadingButton
                  className='h-[30px] w-[120px] !rounded-[0px] !bg-[#0067B8] text-white'
                  type={'submit'}
                  form='login-form'
                  variant={'contained'}
                  loading={loginMutate.isPending}
                >
                  Login
                </LoadingButton>
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
};

export default LoginForm;
